import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@tonic/shared/components/ui';
import {
  FileText,
  BookOpen,
  HelpCircle,
  Download,
  ExternalLink,
  PlayCircle,
} from 'lucide-react';
/**
 * @typedef {Object} ResaleItem
 * @property {*} id;
 * @property {*} facility;
 * @property {*} dateReceived;
 * @property {*} status;
 * @property {*} resaleValue;
 */
const mockResaleItems = [
  {
    id: 'LRPLY00545897',
    facility: 'USCA005\nCalifornia, United States',
    dateReceived: '10 Oct 23 at 10:43',
    status: 'Listed',
    resaleValue: 43,
  },
  {
    id: 'LRPLY00354861',
    facility: 'CAON015\nOntario, United States',
    dateReceived: '10 Oct 23 at 10:42',
    status: 'Listed',
    resaleValue: 50,
  },
];

const guides = [
  {
    id: 'getting-started',
    title: 'Getting Started with Resale',
    description: 'Learn the basics of managing resale items and listings.',
    icon: BookOpen,
    link: '/docs/resale/getting-started',
  },
  {
    id: 'item-assessment',
    title: 'Item Assessment Guide',
    description: 'How to assess item condition and set resale prices.',
    icon: FileText,
    link: '/docs/resale/assessment',
  },
  {
    id: 'listing-best-practices',
    title: 'Listing Best Practices',
    description: 'Tips for creating effective resale listings.',
    icon: FileText,
    link: '/docs/resale/listing-guide',
  },
  {
    id: 'video-tutorials',
    title: 'Video Tutorials',
    description: 'Watch step-by-step guides for common tasks.',
    icon: PlayCircle,
    link: '/docs/resale/tutorials',
  },
];

const faqs = [
  {
    question: 'How do I determine the resale price for an item?',
    answer: 'Consider factors like original price, condition, market demand, and seasonality. Use the pricing calculator in the item detail view for recommendations.',
  },
  {
    question: 'What are the condition grades and their criteria?',
    answer: 'We use grades from A to D. Grade A is "Like New" with minimal signs of use, B is "Good" with minor wear, C is "Fair" with noticeable wear, and D is "Poor" requiring repair.',
  },
  {
    question: 'How long should I wait before adjusting the price?',
    answer: "Monitor item performance for 14-30 days. If there's no interest, consider a 10-15% price reduction. Use the analytics page to view average time-to-sell metrics.",
  },
  {
    question: 'What photos are required for listings?',
    answer: 'Required photos include: front, back, labels/tags, any defects/wear, and size markings. Additional context photos are recommended for higher-value items.',
  },
];

const resources = [
  {
    title: 'Resale Program Guidelines',
    description: 'Complete documentation of resale policies and procedures.',
    type: 'PDF',
    size: '2.4 MB',
    link: '/docs/resale-guidelines.pdf',
  },
  {
    title: 'Condition Assessment Checklist',
    description: 'Detailed checklist for evaluating item condition.',
    type: 'PDF',
    size: '1.1 MB',
    link: '/docs/condition-checklist.pdf',
  },
  {
    title: 'Product Photography Guide',
    description: 'Guidelines for taking high-quality product photos.',
    type: 'PDF',
    size: '3.2 MB',
    link: '/docs/photo-guide.pdf',
  },
  {
    title: 'Pricing Strategy Template',
    description: 'Excel template for calculating optimal resale prices.',
    type: 'XLSX',
    size: '945 KB',
    link: '/docs/pricing-template.xlsx',
  },
];

export function ResaleInformationPage() {
  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <FileText className="w-8 h-8 text-neutral-500" />
        <div>
          <h1 className="text-2xl font-bold">Information & Resources</h1>
          <p className="text-sm text-neutral-500">Documentation, guides, and resources for the resale program</p>
        </div>
      </div>

      <Tabs defaultValue="guides">
        <TabsList>
          <TabsTrigger value="guides">Quick Start Guides</TabsTrigger>
          <TabsTrigger value="faq">FAQ</TabsTrigger>
          <TabsTrigger value="resources">Resources</TabsTrigger>
        </TabsList>

        <TabsContent value="guides" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {guides.map((guide) => (
              <Card key={guide.id}>
                <CardHeader className="flex flex-row items-center gap-4">
                  <guide.icon className="w-8 h-8 text-neutral-500" />
                  <div>
                    <CardTitle className="text-lg">{guide.title}</CardTitle>
                    <p className="text-sm text-neutral-500">{guide.description}</p>
                  </div>
                </CardHeader>
                <CardContent>
                  <Button variant="outline" className="w-full" asChild>
                    <a href={guide.link} target="_blank" rel="noopener noreferrer">
                      View Guide
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </a>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="faq">
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <Collapsible key={index}>
                    <CollapsibleTrigger className="flex w-full items-center justify-between py-2 text-left">
                      <div className="flex items-center gap-3">
                        <HelpCircle className="w-5 h-5 text-neutral-500 flex-shrink-0" />
                        <span className="font-medium">{faq.question}</span>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="pl-8 pt-2 pb-4">
                      {faq.answer}
                    </CollapsibleContent>
                  </Collapsible>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="resources">
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-6">
                {resources.map((resource, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between py-4 border-b border-neutral-800 last:border-0"
                  >
                    <div className="space-y-1">
                      <h3 className="font-medium">{resource.title}</h3>
                      <p className="text-sm text-neutral-500">{resource.description}</p>
                      <div className="flex items-center gap-2 text-xs text-neutral-500">
                        <span className="px-2 py-1 rounded-full bg-neutral-800">{resource.type}</span>
                        <span>{resource.size}</span>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" asChild>
                      <a href={resource.link} download>
                        <Download className="w-4 h-4 mr-2" />
                        Download
                      </a>
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
